import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import toast from 'react-hot-toast';
import { Account } from 'shared/types/account';
import { Address } from 'shared/types/account/Address';
import Button from 'components/gruenland/atoms/button/Button';
import Dropdown from 'components/gruenland/atoms/dropdown';
import Input from 'components/gruenland/atoms/input';
import Typography from 'components/gruenland/atoms/typography';
import { AccountContext } from 'context/account';
import { useFormat } from 'helpers/hooks/useFormat';
import useI18n from 'helpers/hooks/useI18n';
import { validateCodiceFiscale, validatePostalCode, validateTextExists } from 'helpers/validation';
import { getCountryFromCode } from 'project.config';
import countryStates from 'static/states.json';
import DeleteModal from './deleteModal';
import usePropsToAddressType from './mapPropsToAddressType';
import AccountForm from '../../account-atoms/account-form';
import SaveOrCancel from '../../account-atoms/save-or-cancel';
import useDiscardForm from '../../hooks/useDiscardForm';
import useFeedbackToasts from '../../hooks/useFeedbackToasts';

export interface AddressFormProps {
  addressId?: string;
  editedAddressId?: Address['addressId'];
}

export interface AddressFormData extends Address {
  addressId: string;
  addressType?: 'shipping' | 'billing';
  isDefaultAddress?: boolean;
  isBillingAddress?: boolean;
  isDefaultBillingAddress?: boolean;
  isDefaultShippingAddress?: boolean;
}

type AddressType = 'shipping' | 'billing';
type AddressTypeOptions = Array<{ label: string; value: AddressType }>;

const AddressForm: React.FC<AddressFormProps> = ({ editedAddressId }) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const { removeAddress, account } = useContext(AccountContext);
  const { mapPropsToAddress } = usePropsToAddressType();
  const { discardForm } = useDiscardForm();
  const { notifyDataUpdated, notifyWentWrong } = useFeedbackToasts();
  const { country } = useI18n();

  const [loading, setLoading] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  //new address data
  const defaultData = useMemo(() => {
    if (!editedAddressId) return { country } as AddressFormData;

    const accountAddress = account?.addresses?.find(
      (address) => address.addressId === editedAddressId,
    ) as AddressFormData;

    if (accountAddress) {
      accountAddress.addressType = mapPropsToAddress(accountAddress).addressType;
    }

    return accountAddress;
  }, [account?.addresses, country, editedAddressId, mapPropsToAddress]);

  const [data, setData] = useState<AddressFormData>(defaultData);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isDeletable = useMemo(() => !(data?.isDefaultBillingAddress || data?.isDefaultShippingAddress), [data]);

  const openDeleteModal = useCallback(() => {
    setShowDeleteModal(true);
  }, []);
  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const addressTypes: AddressTypeOptions = [
    {
      label: formatMessage({ id: 'order.shippingAddress', defaultMessage: 'Indirizzo di spedizione' }),
      value: 'shipping',
    },
    {
      label: formatMessage({ id: 'order.billingAddress', defaultMessage: 'Indirizzo di fatturazione' }),
      value: 'billing',
    },
  ];

  const formTitle = formatMessage(
    editedAddressId
      ? { id: 'address.edit', defaultMessage: 'Modifica indirizzo' }
      : { id: 'address.add', defaultMessage: 'Aggiungi indirizzo' },
  );

  const requireCF = !!(data.country && getCountryFromCode(data.country)?.hasCheckoutCfVat);

  useEffect(() => {
    setData(defaultData);
  }, [defaultData]);

  const updateData = (name: string, value: boolean | string) => {
    setData({ ...data, [name]: value });
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    updateData(e.target.name, e.target.value);
  };

  const discardFormAndNotify = (promise: Promise<Account | void>) => {
    promise
      .then(() => setLoading(false))
      .then(discardForm)
      .then(notifyDataUpdated)
      .catch(notifyWentWrong);
  };

  const handleDelete = () => {
    setLoadingDelete(true);

    removeAddress(data.addressId)
      .then(() => setLoadingDelete(false))
      .then(closeDeleteModal)
      .then(() =>
        toast.success(formatMessage({ id: 'address.deleted', defaultMessage: 'Indirizzo eliminato con successo.' })),
      )
      .then(discardForm);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { addAddress, updateAddress } = mapPropsToAddress(data);

    const errors: string[] = [];

    // Fiscal code validation
    if (currentAddressType === 'billing' && requireCF) {
      if (!data.fiscalCode?.trim()) {
        errors.push(
          formatMessage({ id: 'address.fiscalCode.required', defaultMessage: 'Il codice fiscale è obbligatorio.' }),
        );
      } else {
        const fiscalCodeValid = validateCodiceFiscale(data.fiscalCode);
        if (!fiscalCodeValid) {
          errors.push(
            formatMessage({ id: 'address.fiscalCode.invalid', defaultMessage: 'Il codice fiscale non è valido.' }),
          );
        }
      }
    }

    if (errors.length > 0) {
      errors.forEach((err) => toast.error(err));
      return;
    }

    setLoading(true);

    if (editedAddressId) {
      if (defaultData.addressType !== data.addressType) {
        discardFormAndNotify(removeAddress(defaultData.addressId).then(addAddress));
      } else {
        discardFormAndNotify(updateAddress());
      }

      return;
    }
    discardFormAndNotify(addAddress());
  };

  const currentAddressType = data.addressType ?? addressTypes[0].value;

  const provinces = useMemo(() => {
    const provinces = countryStates[data.country as keyof typeof countryStates] ?? [];
    if (!provinces.length) return [];
    return [{ label: '', value: '' }, ...provinces.map(({ name, code }) => ({ label: name, value: code }))];
  }, [data.country]);

  return (
    <AccountForm
      onSubmit={handleSubmit}
      title={formTitle}
      loading={loading}
      containerClassName="grid gap-3 md:px-6 md:px-0"
    >
      {!editedAddressId && (
        <Dropdown
          name="addressType"
          items={addressTypes}
          className="w-full border-neutral-500"
          onChange={handleChange}
          value={currentAddressType}
          label={formatMessage({
            id: 'address.type',
            defaultMessage: 'Tipo di indirizzo',
          })}
        />
      )}

      <Input
        label={formatMessage({ id: 'user.firstName', defaultMessage: 'Nome' })}
        required
        type="text"
        name="firstName"
        id="first-name"
        value={data?.firstName ?? ''}
        autoComplete="first-name"
        className="border-neutral-500"
        onChange={handleChange}
        validation={validateTextExists}
      />

      <Input
        label={formatMessage({ id: 'user.lastName', defaultMessage: 'Cognome' })}
        required
        type="text"
        name="lastName"
        id="last-name"
        value={data?.lastName ?? ''}
        autoComplete="last-name"
        className="border-neutral-500"
        onChange={handleChange}
        validation={validateTextExists}
      />

      {currentAddressType === 'billing' && (
        <>
          <Input
            label={`${formatMessage({ id: 'address.company', defaultMessage: 'Ragione sociale' })} (${formatMessage({ id: 'common.optional', defaultMessage: 'Opzionale' })})`}
            type="text"
            name="company"
            id="company"
            value={data?.company ?? ''}
            autoComplete="company"
            className="border-neutral-500"
            onChange={handleChange}
            validation={validateTextExists}
          />

          {requireCF && (
            <>
              <Input
                label={formatMessage({ id: 'address.fiscalCode', defaultMessage: 'Codice fiscale' })}
                type="text"
                required
                name="fiscalCode"
                id="fiscal-code"
                value={data?.fiscalCode ?? ''}
                className="border-neutral-500"
                onChange={handleChange}
              />

              <Input
                label={`${formatMessage({ id: 'address.vatId', defaultMessage: 'Partita IVA' })} (${formatMessage({ id: 'common.optional', defaultMessage: 'Opzionale' })})`}
                type="text"
                name="vatId"
                id="vat-id"
                value={data?.vatId ?? ''}
                className="border-neutral-500"
                onChange={handleChange}
              />
            </>
          )}
        </>
      )}

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-3 lg:col-span-2">
          <Input
            label={`${formatMessage({ id: 'common.address', defaultMessage: 'Indirizzo' })} 1`}
            type="text"
            required
            name="streetName"
            id="street-name"
            value={data?.streetName ?? ''}
            autoComplete="primary-address"
            className="border-neutral-500"
            onChange={handleChange}
          />
        </div>
        <div className="col-span-3 lg:col-span-1">
          <Input
            label={`${formatMessage({ id: 'address.streetNumber', defaultMessage: 'Numero civico' })} (${formatMessage({ id: 'common.optional', defaultMessage: 'Opzionale' })})`}
            type="text"
            name="streetNumber"
            id="street-number"
            value={data?.streetNumber ?? ''}
            className="border-neutral-500"
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-3 md:col-span-1">
          <Input
            label={formatMessage({ id: 'address.zipCode', defaultMessage: 'CAP' })}
            required
            type="text"
            name="postalCode"
            id="postal-code"
            value={data?.postalCode ?? ''}
            autoComplete="postal-code"
            className="border-neutral-500"
            validation={validatePostalCode}
            onChange={handleChange}
          />
        </div>

        <div className="col-span-3 md:col-span-2">
          <Input
            label={formatMessage({ id: 'address.city', defaultMessage: 'Città' })}
            required
            type="text"
            name="city"
            id="city"
            value={data?.city ?? ''}
            autoComplete="address-level2"
            className="border-neutral-500"
            onChange={handleChange}
            validation={validateTextExists}
          />
        </div>
      </div>

      {provinces.length > 0 ? (
        <Dropdown
          name="province"
          required
          value={data.province ?? ''}
          items={provinces}
          className="w-full border-neutral-500"
          onChange={handleChange}
          label={formatMessage({
            id: 'address.province',
            defaultMessage: 'Provincia',
          })}
        />
      ) : (
        <Input
          label={formatMessage({ id: 'address.province', defaultMessage: 'Provincia' })}
          type="text"
          name="province"
          id="province"
          value={data?.province ?? ''}
          autoComplete="address-level1"
          className="border-neutral-500"
          onChange={handleChange}
          validation={validateTextExists}
        />
      )}

      <div className="grid h-fit items-center justify-between gap-8 md:mt-5 md:flex md:gap-0">
        <div>
          {(data?.isDefaultBillingAddress || data?.isDefaultShippingAddress) && (
            <Typography className="text-[14px] leading-[114%] text-secondary-black" as="span">
              {formatMessage({ id: 'common.default', defaultMessage: 'Default' })}
            </Typography>
          )}
          {editedAddressId && isDeletable && (
            <Button
              type="button"
              className={classNames('flex items-center gap-2 hover:cursor-pointer hover:opacity-70')}
              variant="buttonSecondary"
              onClick={openDeleteModal}
            >
              <TrashIcon className="size-5" />
              <Typography className="text-[14px] leading-[114%]" as="span">
                {formatMessage({ id: 'common.delete', defaultMessage: 'Elimina' })}
              </Typography>
            </Button>
          )}
        </div>

        <SaveOrCancel onCancel={discardForm} loading={loading} />
      </div>

      <DeleteModal
        modalIsOpen={showDeleteModal}
        loading={loadingDelete}
        closeModal={closeDeleteModal}
        handleDelete={handleDelete}
      />
    </AccountForm>
  );
};

export default AddressForm;
